// src/components/billing/UnlockSalesModal.tsx
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import UnlockPaywallButton from './UnlockPaywallButton';

interface Props {
  eventId: string;
  ctaLabel?: string;
  priceText?: string;           // kept for backwards compat but unused in UI now
  listenGlobalOpen?: boolean;
  initiallyOpen?: boolean;
  onClose?: () => void;
  currentStep?: number;
}

const UnlockSalesModal: React.FC<Props> = (props) => {
  const {
    eventId,
    ctaLabel = 'UNLOCK & SEND — $4.99',
    // priceText intentionally not rendered (no pills in the header)
    listenGlobalOpen = false,
    initiallyOpen = false,
    onClose: onCloseProp,
    currentStep,
  } = props;

  const [open, setOpen] = useState<boolean>(!!initiallyOpen);

  // Sync with parent
  useEffect(() => {
    if (typeof initiallyOpen === 'boolean') setOpen(initiallyOpen);
  }, [initiallyOpen]);

  // Re-open after Stripe cancel
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'cancelled') {
      setOpen(true);
      try { (window as any).gtag?.('event', 'sales_modal_shown', { reason: 'checkout_canceled' }); } catch {}
    }
  }, []);

  // Global open
  useEffect(() => {
    if (!listenGlobalOpen) return;
    const handler = () => {
      setOpen(true);
      try { (window as any).gtag?.('event', 'sales_modal_shown', { reason: 'global_event' }); } catch {}
    };
    window.addEventListener('spark:openUnlockModal', handler);
    return () => window.removeEventListener('spark:openUnlockModal', handler);
  }, [listenGlobalOpen]);

  const onClose = () => {
    setOpen(false);
    try { (window as any).gtag?.('event', 'sales_modal_closed'); } catch {}
    onCloseProp?.();
  };

  if (!open) return null;

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(12, 8, 28, 0.72)',
        backdropFilter: 'blur(4px) saturate(115%)',
        WebkitBackdropFilter: 'blur(4px) saturate(115%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        overscrollBehavior: 'contain',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(640px, 92vw)',
          background: '#ffffff',
          borderRadius: 24,
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          border: '1px solid rgba(87, 69, 225, 0.20)',
          padding: '24px 24px 28px',
          color: '#110A2E',
          overflow: 'visible',
        }}
      >
        {/* Header */}
        <h2
          style={{
            margin: 0,
            textAlign: 'center',
            fontWeight: 900,
            fontSize: 28,
            letterSpacing: 0.2,
            color: '#24124D',
          }}
        >
          Unlock Distribution
        </h2>
        <p
          style={{
            margin: '6px 0 16px',
            textAlign: 'center',
            fontSize: 16,
            color: '#5846B6',
            fontWeight: 600,
          }}
        >
          Send & track invites in seconds
        </p>

        {/* Bullets (unchanged) */}
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 18px 0', fontSize: 16 }}>
          {[
            'Share by text, email, or link',
            'Get instant RSVPs & easily track guests',
            'Add up to 200 guests per event',
            'Secure, instant checkout with Stripe',
          ].map((t, i) => (
            <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', margin: '10px 0' }}>
              <span aria-hidden style={{ color: '#2DBE7F', fontWeight: 900 }}>✓</span>
              <span>{t}</span>
            </li>
          ))}
        </ul>

        {/* CTA — big primary button look, with the real UnlockPaywallButton full-cover + invisible */}
        <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div
              style={{
                position: 'relative',
                width: 'min(520px, 100%)',
                minHeight: 56,
                background: '#4D57F5',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 800,
                letterSpacing: 0.2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(77, 87, 245, 0.35)',
                padding: '14px 18px',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              {ctaLabel}

              {/* The actual functional button */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  // Ensure clicks go to the real button (which triggers Stripe)
                  display: 'flex',
                  alignItems: 'stretch',
                  justifyContent: 'stretch',
                  opacity: 0,
                }}
              >
                <UnlockPaywallButton
                  eventId={eventId}
                  label={ctaLabel}
                  currentStep={currentStep}
                />
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#4D57F5',
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
              textDecoration: 'underline',
              alignSelf: 'center',
            }}
          >
            Maybe later
          </button>
        </div>

        {/* Footnote */}
        <div style={{ marginTop: 12, textAlign: 'center', color: '#6B63C4', fontSize: 13 }}>
          Instant secure checkout • Promo codes accepted
        </div>
        <div style={{ marginTop: 6, textAlign: 'center', color: '#7B83E9', fontSize: 12 }}>
          Powered by Stripe
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
};

export default UnlockSalesModal;
